import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { ArrowLeft, ArrowRight, Brain } from 'lucide-react';

const scaleOptions = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Slightly Disagree' },
  { value: 4, label: 'Slightly Agree' },
  { value: 5, label: 'Agree' },
  { value: 6, label: 'Strongly Agree' }
];

const defaultQuestions = [
  { id: 1, text: 'When I hear about an investment that has delivered exceptional returns, my first instinct is to find a way to get involved.', metric: 'gain_attraction' },
  { id: 2, text: 'If I won an amount of money and were offered to flip a coin which would either double the value or lose it, I would flip the coin.', metric: 'gain_attraction', reverse: true },
  { id: 3, text: 'I would rather earn a smaller, reliable return than chase a higher but uncertain return.', metric: 'gain_attraction', reverse: true },
  { id: 4, text: 'The mere possibility of a significant upside is enough to excite me about an investment, even before I\'ve fully assessed the risks.', metric: 'gain_attraction' },
  { id: 5, text: 'When markets go down, I would see it as a chance to buy good investments at lower prices, rather than a reason to stop investing.', metric: 'gain_attraction' },
  { id: 6, text: 'I would be willing to pay someone to manage my investment if it could improve my chance of earning higher returns.', metric: 'gain_attraction' },
  { id: 7, text: 'If one of my investments dropped sharply in value, I would prefer to sell it rather than risk losing even more money.', metric: 'loss_aversion' },
  { id: 8, text: 'I\'d rather keep money in a low-interest savings account than risk losing it in the market, even over a long time horizon.', metric: 'loss_aversion' },
  { id: 9, text: 'When one of my investments drops significantly in value, I tend to think about it frequently until it recovers.', metric: 'loss_aversion' },
  { id: 10, text: 'I often delay investment decisions because I\'m worried about making the wrong call.', metric: 'loss_aversion' },
  { id: 11, text: 'I view financial losses as an unavoidable part of the investment process rather than something to take personally.', metric: 'loss_aversion', reverse: true },
  { id: 12, text: 'If one of my investments did much better than expected, I would rather sell it than risk losing my gains.', metric: 'loss_aversion' },
  { id: 13, text: 'I would feel anxious if I saw large fluctuations in the value of my investments.', metric: 'volatility_anxiety' },
  { id: 14, text: 'I would stay calm, even if my investments dropped by a significant amount.', metric: 'volatility_anxiety', reverse: true },
  { id: 15, text: 'I can go weeks without checking my portfolio balance and feel completely at ease doing so.', metric: 'volatility_anxiety', reverse: true },
  { id: 16, text: 'If I heard news of the stock market falling, I would be very concerned for the value of my own investments.', metric: 'volatility_anxiety' },
  { id: 17, text: 'I would be more interested in predictable investment performance than taking extra risks to pursue higher gains.', metric: 'volatility_anxiety' },
  { id: 18, text: 'Short-term losses would not cause me to worry about the long-term prospects of my investments.', metric: 'volatility_anxiety', reverse: true },
  { id: 19, text: 'If I hear bad news about markets, my first instinct would be to do something about my investments.', metric: 'intervention_risk' },
  { id: 20, text: 'When my investments are not doing what I expected, I prefer to act rather than wait.', metric: 'intervention_risk' },
  { id: 21, text: 'I am unlikely to be distracted by the latest investment trends. I prefer tried and tested strategies.', metric: 'intervention_risk', reverse: true },
  { id: 22, text: 'I would want to change my investment if it started losing value.', metric: 'intervention_risk' },
  { id: 23, text: 'I would feel more comfortable knowing experts are choosing funds and managing my investments, instead of doing it myself.', metric: 'intervention_risk', reverse: true },
  { id: 24, text: 'I prefer being personally involved in managing my investments.', metric: 'intervention_risk' },
];

const PersonalityAssessment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const answersRef = useRef({});
  const submittingRef = useRef(false);
  const advancingRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Fetch questions from backend API
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await api.get('/questions/personality');
        const backendQuestions = response.data
          .sort((a, b) => a.display_order - b.display_order)
          .map(q => ({
            id: q.id,
            text: q.question_text,
            metric: q.metric_mapping,
            reverse: q.reverse_scored
          }));
        setQuestions(backendQuestions.length > 0 ? backendQuestions : defaultQuestions);
      } catch (err) {
        console.error('Failed to fetch questions, using defaults:', err);
        setQuestions(defaultQuestions);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  // Clamp the displayed index so a momentary overshoot during the final
  // submit transition can never render an undefined question (white screen).
  const safeIndex = questions.length > 0 ? Math.min(currentIndex, questions.length - 1) : 0;
  const currentQuestion = questions[safeIndex];
  const progress = ((safeIndex + 1) / questions.length) * 100;

  const handleAnswer = (value) => {
    if (submittingRef.current || advancingRef.current) return;

    const updated = { ...answersRef.current, [currentQuestion.id]: value };
    answersRef.current = updated;
    setAnswers(updated);

    if (currentIndex < questions.length - 1) {
      advancingRef.current = true;
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        advancingRef.current = false;
      }, 200);
    } else {
      submitAnswers(updated);
    }
  };

  const handleNext = async () => {
    if (submittingRef.current) return;

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      await submitAnswers(answersRef.current);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const submitAnswers = async (answersObj) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const answerArray = Object.entries(answersObj).map(([questionId, value]) => ({
        question_id: parseInt(questionId),
        answer_value: value
      }));

      await api.post(`/assessments/${id}/personality-answers`, { answers: answerArray });
      await api.patch(`/assessments/${id}/status`, { status: 'submitted' });
      navigate(`/assessment/${id}/complete`);
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      setSubmitError(error.response?.data?.error || error.message || 'Failed to submit assessment');
      submittingRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const getScoreColor = (value) => {
    if (value <= 2) return 'text-red-500';
    if (value <= 4) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20">
            <Brain className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: theme.primary }} />
            <p style={{ color: theme.textSecondary }}>Loading questions...</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-20">
            <p style={{ color: theme.textSecondary }}>No questions available.</p>
          </div>
        ) : (
        <>
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span style={{ color: theme.textSecondary }}>Question {currentIndex + 1} of {questions.length}</span>
            <span style={{ color: theme.primary }}>{Math.round(progress)}% complete</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: theme.border }}>
            <div
              className="h-full transition-all duration-300 rounded-full"
              style={{ width: `${progress}%`, backgroundColor: theme.primary }}
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#8b5cf620' }}>
                <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>
                  Financial Personality Assessment
                </p>
                <h1 className="text-lg font-bold mt-1" style={{ color: theme.text }}>
                  {currentQuestion.text}
                </h1>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-2 mb-8">
              {scaleOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  disabled={submitting}
                  className="w-full text-left p-4 rounded-lg border-2 transition-all flex items-center justify-between"
                  style={{
                    backgroundColor: answers[currentQuestion.id] === option.value ? `${theme.primary}10` : theme.card,
                    borderColor: answers[currentQuestion.id] === option.value ? theme.primary : theme.border,
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  <span className="font-medium" style={{ color: theme.text }}>
                    {option.label}
                  </span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${getScoreColor(option.value)}`}>
                    {option.value}
                  </span>
                </button>
              ))}
            </div>

            <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
              <p className="text-sm" style={{ color: theme.text }}>
                <strong>Tip:</strong> Answer honestly, there are no right or wrong answers. The more strongly you feel about your answer, the more reliable your results will be.
              </p>
            </div>

            {submitError && <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>}

            <div className="flex justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="flex items-center whitespace-nowrap"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={answers[currentQuestion.id] === undefined || submitting}
                className="flex items-center whitespace-nowrap"
              >
                {submitting ? 'Submitting...' : currentIndex === questions.length - 1 ? 'Complete Assessment' : 'Next'}{' '}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
        </>
        )}
      </div>
    </div>
  );
};

export default PersonalityAssessment;
